import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Star, 
  Users, 
  Quote, 
  ArrowRight, 
  Play, 
  X,
  TrendingUp,
  Clock,
  Award,
  CheckCircle2,
  MessageCircle
} from 'lucide-react';

const testimonialsData = [
  {
    id: 1,
    name: 'Ana López',
    role: 'Distribuidora Senior',
    company: 'DXN Colombia',
    quote: 'Mi DXN transformó mi gestión de productos en una plataforma escalable que maneja 500+ distribuidores simultáneos. Su expertise en tecnología superó todas mis expectativas.',
    avatar: '/images/testimonials/ana-lopez.jpg',
    rating: 5,
    project: 'Plataforma Mi DXN',
    results: ['+50% productividad', '100% datos actualizados', '5★ en satisfacción']
    // No es featured, va a la cuadrícula
  },
  {
    id: 2,
    name: 'Carlos Ruiz',
    role: 'Distribuidor Independiente',
    company: 'DXN Perú',
    quote: 'Las soluciones de Mi DXN me ahorraron más de 20 horas semanales en procesos manuales. La automatización de cálculos de PV/SV es simplemente brillante.',
    avatar: '/images/testimonials/carlos-ruiz.jpg',
    rating: 5,
    project: 'Sistema de Gestión',
    results: ['-20h/semana', '0 errores en cálculos', 'Soporte 24/7'],
    featured: true
  },
  {
    id: 3,
    name: 'María González',
    role: 'Distribuidora Senior',
    company: 'DXN México',
    quote: 'El catálogo digital de Mi DXN es increíblemente rápido. Ahora puedo presentar productos a mis prospectos con total profesionalismo desde mi celular.',
    avatar: '/images/testimonials/maria-gonzalez.jpg',
    rating: 5,
    project: 'Catálogo Digital',
    results: ['+30% ventas', 'Presentación profesional', 'Fácil de usar'],
    featured: true
  },
  {
    id: 4,
    name: 'Juan Pérez',
    role: 'Distribuidor Líder',
    company: 'DXN Argentina',
    quote: 'Mi DXN no solo desarrolló mi plataforma, sino que se convirtió en mi partner tecnológico estratégico. El verificador de DNI nos ahorró muchos dolores de cabeza.',
    avatar: '/images/testimonials/juan-perez.jpg',
    rating: 5,
    project: 'Ecosistema Completo',
    results: ['+40% rangos', '3 nuevos equipos', '0 duplicidades'],
    featured: true
  },
  {
    id: 5,
    name: 'Laura Martínez',
    role: 'Distribuidora de Éxito',
    company: 'DXN Chile',
    quote: 'La implementación del sistema fue impecable. Mi DXN entendió perfectamente mis necesidades y entregó una solución que optimizó toda mi gestión de eventos.',
    avatar: '/images/testimonials/laura-martinez.jpg',
    rating: 5,
    project: 'Gestión de Eventos',
    results: ['-30% tiempo', '+25% eficiencia', 'Gestión centralizada']
    // No es featured, va a la cuadrícula
  },
  {
    id: 6,
    name: 'Roberto Silva',
    role: 'Distribuidor Premium',
    company: 'DXN Brasil',
    quote: 'Las herramientas de Mi DXN son exactamente lo que la red necesitaba. Poder calcular mis puntos al instante y verificar socios me da una ventaja competitiva enorme.',
    avatar: '/images/testimonials/roberto-silva.jpg',
    rating: 5,
    project: 'Herramientas Esenciales',
    results: ['+15% prospectos', 'Cálculos en tiempo real', 'Ventaja competitiva']
    // No es featured, va a la cuadrícula
  }
];

const Testimonials = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);

  // Ahora tenemos exactamente 3 destacados y 3 regulares para un balance perfecto
  const featuredTestimonials = testimonialsData.filter(t => t.featured);
  const regularTestimonials = testimonialsData.filter(t => !t.featured);
  const currentTestimonial = featuredTestimonials[currentIndex];

  // Auto-rotación suave
  useEffect(() => {
    if (isVideoModalOpen) return;
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % featuredTestimonials.length);
    }, 7000);
    return () => clearInterval(interval);
  }, [isVideoModalOpen, featuredTestimonials.length]);

  const StarRating = ({ rating }) => (
    <div className="flex gap-1">
      {[...Array(5)].map((_, i) => (
        <Star
          key={i}
          className={`w-4 h-4 ${i < rating ? 'text-amber-400 fill-amber-400' : 'text-stone-300'}`}
        />
      ))}
    </div>
  );

  return (
    <section id="testimonios" className="py-24 lg:py-32 bg-stone-50 relative overflow-hidden">
      {/* Elementos decorativos de fondo sutiles */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-emerald-100/40 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-amber-100/40 rounded-full blur-3xl" />
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        
        {/* Encabezado */}
        <motion.div
          className="text-center mb-20 max-w-3xl mx-auto"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <div className="inline-flex items-center gap-2 bg-emerald-100 text-emerald-700 px-4 py-1.5 rounded-full text-sm font-semibold mb-6">
            <Users className="w-4 h-4" />
            <span>Comunidad Mi DXN</span>
          </div>
          
          <h2 className="text-4xl md:text-5xl font-bold text-stone-900 mb-6 tracking-tight">
            Historias de <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-600 to-teal-600">Éxito Real</span>
          </h2>
          
          <p className="text-lg text-stone-600 leading-relaxed">
            Descubre cómo distribuidores de todo el mundo están potenciando su productividad y crecimiento en la red con nuestras herramientas.
          </p>
        </motion.div>

        {/* Testimonio Principal Destacado (Carrusel de 3) */}
        <motion.div
          className="relative max-w-5xl mx-auto mb-24"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <div className="bg-white rounded-3xl shadow-xl shadow-stone-900/5 border border-stone-100 p-8 md:p-12 lg:p-16 relative overflow-hidden">
            <Quote className="absolute top-8 left-8 w-24 h-24 text-emerald-100 -z-0" />
            
            <AnimatePresence mode="wait">
              <motion.div
                key={currentTestimonial.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.5 }}
                className="relative z-10 text-center"
              >
                <StarRating rating={currentTestimonial.rating} />
                
                <blockquote className="text-2xl md:text-3xl lg:text-4xl font-medium text-stone-800 leading-snug my-8 max-w-4xl mx-auto">
                  "{currentTestimonial.quote}"
                </blockquote>

                <div className="flex flex-col items-center gap-4 mb-10">
                  <div className="w-16 h-16 bg-gradient-to-br from-emerald-500 to-teal-500 rounded-full flex items-center justify-center text-white font-bold text-xl ring-4 ring-emerald-50">
                    {currentTestimonial.name.split(' ').map(n => n[0]).join('')}
                  </div>
                  <div className="text-center">
                    <h4 className="text-lg font-bold text-stone-900">{currentTestimonial.name}</h4>
                    <p className="text-stone-500">{currentTestimonial.role} • {currentTestimonial.company}</p>
                  </div>
                </div>

                {/* Resultados como Mini-Tarjetas */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 max-w-3xl mx-auto">
                  {currentTestimonial.results.map((result, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.2 + index * 0.1 }}
                      className="bg-stone-50 rounded-xl p-4 border border-stone-100 flex items-center justify-center gap-3"
                    >
                      {index === 0 && <TrendingUp className="w-5 h-5 text-emerald-600" />}
                      {index === 1 && <Clock className="w-5 h-5 text-amber-600" />}
                      {index === 2 && <Award className="w-5 h-5 text-teal-600" />}
                      <span className="font-semibold text-stone-700 text-sm">{result}</span>
                    </motion.div>
                  ))}
                </div>

                {currentTestimonial.videoTestimonial && (
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => setIsVideoModalOpen(true)}
                    className="mt-8 inline-flex items-center gap-2 text-emerald-700 font-semibold hover:text-emerald-800 transition-colors group"
                  >
                    <div className="w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center group-hover:bg-emerald-200 transition-colors">
                      <Play className="w-4 h-4 fill-emerald-700 text-emerald-700 ml-0.5" />
                    </div>
                    Ver testimonio en video
                  </motion.button>
                )}
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Indicadores de navegación (3 puntos) */}
          <div className="flex justify-center gap-3 mt-8">
            {featuredTestimonials.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentIndex(index)}
                className={`h-2 rounded-full transition-all duration-500 ${
                  index === currentIndex ? 'w-8 bg-emerald-600' : 'w-2 bg-stone-300 hover:bg-stone-400'
                }`}
                aria-label={`Ir al testimonio ${index + 1}`}
              />
            ))}
          </div>
        </motion.div>

        {/* Grid de Testimonios Secundarios (AHORA SON EXACTAMENTE 3) */}
        <div className="mb-20">
          <h3 className="text-2xl font-bold text-stone-900 text-center mb-10">Más experiencias de nuestra red</h3>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {regularTestimonials.map((testimonial, index) => (
              <motion.div
                key={testimonial.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                viewport={{ once: true }}
                whileHover={{ y: -5 }}
                className="bg-white rounded-2xl p-8 border border-stone-100 shadow-sm hover:shadow-xl hover:shadow-emerald-900/5 hover:border-emerald-200 transition-all duration-300 flex flex-col h-full"
              >
                <StarRating rating={testimonial.rating} />
                
                <blockquote className="text-stone-600 my-5 leading-relaxed flex-grow">
                  "{testimonial.quote}"
                </blockquote>

                {/* Resultado destacado como badge */}
                <div className="mb-6">
                  <span className="inline-flex items-center gap-1.5 bg-emerald-50 text-emerald-700 px-3 py-1.5 rounded-lg text-xs font-bold border border-emerald-100">
                    <CheckCircle2 className="w-3.5 h-3.5" />
                    {testimonial.results[0]}
                  </span>
                </div>

                <div className="flex items-center gap-4 pt-4 border-t border-stone-100 mt-auto">
                  <div className="w-10 h-10 bg-stone-200 rounded-full flex items-center justify-center text-stone-600 font-bold text-sm">
                    {testimonial.name.split(' ').map(n => n[0]).join('')}
                  </div>
                  <div>
                    <h5 className="font-bold text-stone-900 text-sm">{testimonial.name}</h5>
                    <p className="text-xs text-stone-500">{testimonial.role}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* CTA Section */}
        <motion.div
          className="relative rounded-3xl overflow-hidden"
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <div className="absolute inset-0 bg-gradient-to-br from-emerald-800 via-teal-800 to-emerald-900" />
          <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-10" />
          
          <div className="relative z-10 px-6 py-16 md:py-20 text-center text-white max-w-4xl mx-auto">
            <h3 className="text-3xl md:text-4xl font-bold mb-6">
              ¿Listo para transformar tu negocio con Mi DXN?
            </h3>
            <p className="text-emerald-100 mb-10 text-lg leading-relaxed">
              Únete a los cientos de distribuidores que ya usan nuestra plataforma para ahorrar tiempo, evitar errores y multiplicar sus resultados en la red.
            </p>


            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <motion.a
                href="https://wa.me/51968039152?text=Hola,%20quiero%20comenzar%20ahora%20con%20Mi%20DXN"
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="bg-white text-emerald-900 px-8 py-4 rounded-xl font-bold shadow-xl hover:shadow-2xl transition-all inline-flex items-center justify-center gap-2"
              >
                Comenzar Ahora
                <ArrowRight className="w-5 h-5" />
              </motion.a>

              <motion.a
                href="https://wa.me/51968039152?text=Hola,%20quisiera%20hablar%20con%20un%20asesor%20de%20Mi%20DXN"
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="bg-emerald-700/50 backdrop-blur-sm text-white border border-emerald-400/30 px-8 py-4 rounded-xl font-bold hover:bg-emerald-700/70 transition-all inline-flex items-center justify-center gap-2"
              >
                <MessageCircle className="w-5 h-5" />
                Hablar con un Asesor
              </motion.a>
            </div>


          </div>
        </motion.div>
      </div>

      {/* Modal de Video */}
      <AnimatePresence>
        {isVideoModalOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-stone-950/90 backdrop-blur-sm flex items-center justify-center z-[60] p-4"
            onClick={() => setIsVideoModalOpen(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="relative max-w-4xl w-full bg-black rounded-2xl overflow-hidden shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={() => setIsVideoModalOpen(false)}
                className="absolute top-4 right-4 z-10 bg-white/10 hover:bg-white/20 text-white p-2 rounded-full transition-colors backdrop-blur-md"
                aria-label="Cerrar video"
              >
                <X className="w-6 h-6" />
              </button>
              
              <div className="relative aspect-video">
                <video
                  controls
                  autoPlay
                  className="w-full h-full object-cover"
                >
                  <source src={currentTestimonial.videoTestimonial} type="video/mp4" />
                  Tu navegador no soporta el elemento de video.
                </video>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default Testimonials;